// src/components/Registration/steps/Step2Contacts.jsx
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { nfpApi } from '../../../api/nfpApi';

const PEACH = '#EBA477';
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

/* -------------------- START: STABLE VALIDATION HELPERS (OUTSIDE COMPONENT) -------------------- */
const emailOk = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e || '');
const phoneOk = (p) => /^[+]?[0-9\s\-()]{7,}$/.test((p || '').trim());
const pctOk = (s) => !isNaN(Number(s)) && Number(s) >= 2 && Number(s) <= 10;
const descOk = (s) => typeof s === 'string' && s.trim().length > 0 && s.trim().length <= 128;
const urlOk = (url) => /^https?:\/\/.+/.test(url || '');
const logoOk = (f) => !!f.logoUrl;

const formatEin = (s) => {
  const d = (s || '').replace(/\D+/g, '').slice(0, 9);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}-${d.slice(2)}`;
};
const einOk = (ein) => /^\d{2}-\d{7}$/.test(formatEin(ein) || '');

const OWNERSHIP = ['Independent', 'Franchisee', 'Corporate'];
const ownershipOk = (s) => OWNERSHIP.includes(s);

const OWNERSHIP_HELP = {
  Independent: 'You created and own this business/concept.',
  Franchisee:  'You operate this business under a formal franchise agreement.',
  Corporate:   'Centrally managed by a larger parent company.',
};

// Keep in sync with Brand.category enum
const BRAND_CATEGORIES = [
  'Grocery','Restaurants','Cafe & Bakery','Bars & Breweries',
  'Apparel & Accessories','Health & Beauty','Wellness & Fitness',
  'Electronics & Tech','Home & Garden','Automotive',
  'Sports & Outdoors','Entertainment & Recreation','Arts & Culture',
  'Office & Professional Services','Education & Childcare',
  'Pet Supplies & Services','Travel & Hospitality',
  'Financial & Insurance Services','Specialty Retail',
  'Home Services & Trades','Real Estate & Property','Other'
];

// 2% → 10% in 1% steps
const baselineOptions = Array.from({ length: 9 }, (_, i) => {
  const n = 2 + i;
  return { value: String(n), label: `${n}%` };
});

const SEARCH_GRANULARITY_OPTIONS = [
  { value: 'state', label: 'State' },
  { value: 'area', label: 'City' },
  { value: 'zip', label: 'ZIP Code' },
];

const STATE_OPTIONS = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'],
  ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'],
  ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'],
  ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'],
  ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'],
  ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
  ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'],
  ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'],
  ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'],
  ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'], ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];
const STATE_LABEL_BY_CODE = Object.fromEntries(
  STATE_OPTIONS.map(([code, label]) => [code, label])
);
const VALID_STATE_CODES = new Set(STATE_OPTIONS.map(([code]) => code));
/* -------------------- END: STABLE VALIDATION HELPERS -------------------- */

const dedupeStrings = (arr = [], limit = 10) =>
  Array.from(
    new Set(
      (Array.isArray(arr) ? arr : [])
        .map((s) => String(s || '').trim())
        .filter(Boolean)
    )
  ).slice(0, limit);

const dedupeLocationSearchValues = (arr = [], mode = '', limit) => {
  const max = Number.isFinite(limit)
    ? limit
    : mode === 'state'
    ? STATE_OPTIONS.length
    : 10;

  const values = (Array.isArray(arr) ? arr : [])
    .map((entry) => String(entry || '').trim())
    .filter(Boolean)
    .map((entry) => {
      if (mode === 'state') return entry.toUpperCase();
      if (mode === 'zip') return entry.replace(/\D+/g, '').slice(0, 5);
      return entry;
    })
    .filter((entry) => {
      if (mode === 'state') return VALID_STATE_CODES.has(entry);
      if (mode === 'zip') return /^\d{5}$/.test(entry);
      return true;
    });

  return Array.from(new Set(values)).slice(0, max);
};

const resolveLegacySearchValues = (brand = {}) => {
  const fromInput = Array.isArray(brand?.marketsInput?.regionalList)
    ? brand.marketsInput.regionalList
    : [];
  if (fromInput.length) return dedupeStrings(fromInput, 10);

  const fromMarkets = Array.isArray(brand?.markets)
    ? brand.markets.map((m) => m?.marketKey || m?.name || m)
    : [];
  return dedupeStrings(fromMarkets, 10);
};

const normalizeLocationSearch = (raw = {}, fallbackSearchName = '', { commitPending = false, trimSearchName = false } = {}) => {
  const fallbackValues = resolveLegacySearchValues(raw);
  const fallbackMode = fallbackValues.length ? 'area' : '';
  const requestedMode = String(raw?.mode || '').trim().toLowerCase();
  const mode = ['state', 'area', 'zip'].includes(requestedMode) ? requestedMode : fallbackMode;
  const fallbackName = String(fallbackSearchName || '').trim();
  const rawSearchName =
    typeof raw?.searchName === 'string'
      ? raw.searchName
      : fallbackName;
  const explicitUsePublicName = typeof raw?.usePublicName === 'boolean' ? raw.usePublicName : undefined;
  const usePublicName =
    explicitUsePublicName ?? (!rawSearchName || rawSearchName.trim() === fallbackName);
  const searchNameSource = usePublicName ? fallbackName : rawSearchName;
  const searchName = trimSearchName ? searchNameSource.trim() : searchNameSource;
  const rawPendingInput = String(raw?.pendingInput ?? '');
  const pendingInput =
    mode === 'state'
      ? rawPendingInput.toUpperCase().trim()
      : mode === 'zip'
      ? rawPendingInput.replace(/\D+/g, '').slice(0, 5)
      : rawPendingInput.replace(/^\s+/, '');
  const baseValues = Array.isArray(raw?.values) && raw.values.length ? raw.values : fallbackValues;
  const values = dedupeLocationSearchValues(
    [...baseValues, ...(commitPending ? [pendingInput] : [])],
    mode,
  );

  return {
    mode,
    values,
    searchName,
    usePublicName,
    pendingInput: commitPending ? '' : pendingInput,
  };
};

const normalizeOwnershipType = (input, footprint) => {
  const raw = String(input || '').trim();
  const lower = raw.toLowerCase();
  if (lower === 'franchise' || lower === 'franchisee') return 'Franchisee';
  if (lower === 'corporate') return 'Corporate';

  const fp = String(footprint || '').toLowerCase();
  if (fp === 'franchise' || fp === 'franchisee') return 'Franchisee';
  if (fp === 'corporate' || fp === 'chain' || fp === 'national' || fp === 'regional') return 'Corporate';

  return 'Independent';
};

const toFootprint = (ownership) => {
  switch (ownership) {
    case 'Franchisee':
      return 'franchisee';
    case 'Corporate':
      return 'national'; // conservative default for corporate/chain
    default:
      return 'local'; // Independent → local by default
  }
};

const emptyBrand = () => ({
  brandName: '',
  businessCategory: '',
  baselineContribution: '2',
  tagline: '',
  description: '',
  logoFile: null,
  logoPreviewUrl: '',
  logoUrl: '',
  isOnlineOnly: false,
  primaryWebsiteUrl: '',
  locationSearch: {
    mode: '',
    values: [],
    searchName: '',
    usePublicName: true,
    pendingInput: '',
  },
  tipEligible: undefined,
  restaurantSubtype: '',
  marketsInput: {
    regionalList: [],
  },
  markets: [],
  marketKey: '',
});

const emptyEinForm = () => ({
  legalBusinessName: '',
  einTaxId: '',
  ownershipType: 'Independent',
  aliases: [],
  primaryContact: { name: '', email: '', phone: '' },
  brands: [emptyBrand()],
});

const sanitizeBrand = (brand = {}) => {
  const base = emptyBrand();
  const rawBrandName =
    typeof brand?.brandName === 'string'
      ? brand.brandName
      : typeof brand?.dbaName === 'string'
      ? brand.dbaName
      : '';
  const fallbackBrandName = typeof rawBrandName === 'string' ? rawBrandName : '';
  const locationSearch = normalizeLocationSearch(
    brand?.locationSearch || brand,
    fallbackBrandName,
    { commitPending: false }
  );

  const normalized = {
    ...base,
    ...brand,
    brandName: fallbackBrandName,
    businessCategory: brand.businessCategory || brand.category || '',
    baselineContribution: String(
      Math.min(10, Math.max(2, Number(brand.baselineContribution ?? brand.baseContributionPct ?? 2) || 2))
    ),
    tagline: typeof brand.tagline === 'string' ? brand.tagline : '',
    description: typeof brand.description === 'string' ? brand.description.slice(0, 128) : '',
    logoFile: brand.logoFile || null,
    logoPreviewUrl: '',
    logoUrl:
      typeof brand.logoUrl === 'string'
        ? brand.logoUrl
        : typeof brand.logo === 'string'
        ? brand.logo
        : typeof brand.logoPreviewUrl === 'string'
        ? brand.logoPreviewUrl
        : '',
    isOnlineOnly: !!brand.isOnlineOnly,
    primaryWebsiteUrl: typeof brand.primaryWebsiteUrl === 'string' ? brand.primaryWebsiteUrl : '',
    locationSearch,
    tipEligible: typeof brand.tipEligible === 'boolean' ? brand.tipEligible : undefined,
    restaurantSubtype:
      ['fine_dining', 'casual', 'fast_casual'].includes(String(brand.restaurantSubtype || '').trim())
        ? String(brand.restaurantSubtype).trim()
        : '',
  };

  const regionalList = locationSearch.values;

  normalized.marketsInput = {
    regionalList,
  };

  normalized.markets = regionalList.map((m) => ({ marketKey: m, name: m }));
  normalized.marketKey = normalized.markets[0]?.marketKey || '';

  return normalized;
};

const sanitizeEinForm = (form = {}) => {
  const base = emptyEinForm();
  const ownershipType = normalizeOwnershipType(form.ownershipType, form.footprint);

  const brandsSource = Array.isArray(form.brands) && form.brands.length
    ? form.brands
    : (() => {
        const primaryBrand = {
          brandName: form.brandName || '',
          businessCategory: form.businessCategory || '',
          baselineContribution: form.baselineContribution ?? '2',
          tagline: form.tagline || '',
          description: form.description || '',
          logoFile: form.logoFile || null,
          logoPreviewUrl: '',
          logoUrl: form.logoUrl || form.logoPreviewUrl || '',
          isOnlineOnly: !!form.isOnlineOnly,
          primaryWebsiteUrl: form.primaryWebsiteUrl || '',
          marketsInput: form.marketsInput,
          markets: form.markets,
        };

        const extras = Array.isArray(form.extraBrandNames)
          ? form.extraBrandNames.map((name) => ({
              brandName: name || '',
            }))
          : [];

        const allBrands = [primaryBrand, ...extras];
        return allBrands.some((b) => (b.brandName || '').trim()) ? allBrands : [emptyBrand()];
      })();

  const normalized = {
    ...base,
    ...form,
    legalBusinessName:
      typeof form.legalBusinessName === 'string'
        ? form.legalBusinessName
        : typeof form.legalEntityName === 'string'
        ? form.legalEntityName
        : '',
    einTaxId: formatEin(form.einTaxId || form.ein || ''),
    ownershipType,
    aliases: dedupeStrings(form.aliases || [], 8),
    primaryContact: {
      name: typeof form?.primaryContact?.name === 'string' ? form.primaryContact.name : '',
      email: typeof form?.primaryContact?.email === 'string' ? form.primaryContact.email : '',
      phone: typeof form?.primaryContact?.phone === 'string' ? form.primaryContact.phone : '',
    },
  };

  normalized.brands = brandsSource.map((brand) => sanitizeBrand(brand));

  return normalized;
};

const hydrateEinFormsFromBusinesses = (businesses = []) => {
  if (!Array.isArray(businesses) || !businesses.length) return null;

  const grouped = new Map();

  businesses.forEach((biz, idx) => {
    const normalizedEin = formatEin(biz.ein || biz.einTaxId || '');
    const key = normalizedEin || `__idx_${idx}`;

    if (!grouped.has(key)) {
      grouped.set(key, {
        ...emptyEinForm(),
        legalBusinessName: (biz.legalEntityName || '').trim(),
        einTaxId: normalizedEin,
        ownershipType: normalizeOwnershipType(biz.ownershipType, biz.footprint),
        aliases: [],
        primaryContact: {
          name: (biz?.primaryContact?.name || '').trim(),
          email: (biz?.primaryContact?.email || '').trim(),
          phone: (biz?.primaryContact?.phone || '').trim(),
        },
        brands: [],
      });
    }

    const bucket = grouped.get(key);

    bucket.legalBusinessName = bucket.legalBusinessName || (biz.legalEntityName || '').trim();
    bucket.ownershipType = normalizeOwnershipType(bucket.ownershipType || biz.ownershipType, biz.footprint);
    bucket.primaryContact = {
      name: (biz?.primaryContact?.name || bucket.primaryContact.name || '').trim(),
      email: (biz?.primaryContact?.email || bucket.primaryContact.email || '').trim(),
      phone: (biz?.primaryContact?.phone || bucket.primaryContact.phone || '').trim(),
    };

    const incomingAliases = Array.isArray(biz.aliases) ? biz.aliases : [];
    bucket.aliases = dedupeStrings([...(bucket.aliases || []), ...incomingAliases], 8);

    bucket.brands.push(
      sanitizeBrand({
        brandName: biz.dbaName,
        businessCategory: biz.category,
        baselineContribution: String(biz.baseContributionPct ?? 2),
        tagline: biz.tagline,
        description: biz.description,
        logoUrl: biz.logoUrl,
        isOnlineOnly: !!biz.isOnlineOnly,
        primaryWebsiteUrl: biz.primaryWebsiteUrl,
        tipEligible: biz.tipEligible,
        restaurantSubtype: biz.restaurantSubtype,
        markets: biz.markets,
      })
    );
  });

  return Array.from(grouped.values()).map(sanitizeEinForm);
};

const hydrateEinForms = (data) => {
  if (!data) return null;
  if (Array.isArray(data.einForms) && data.einForms.length) {
    return data.einForms.map(sanitizeEinForm);
  }
  if (Array.isArray(data.businesses) && data.businesses.length) {
    return hydrateEinFormsFromBusinesses(data.businesses);
  }
  return null;
};

const validateEinForm = (form, idx) => {
  const normalized = sanitizeEinForm(form);
  const errors = {};
  const key = (k) => `${idx}.${k}`;

  if (!normalized.legalBusinessName || !normalized.legalBusinessName.trim()) {
    errors[key('legalBusinessName')] = 'Required';
  }
  if (!einOk(normalized.einTaxId)) errors[key('einTaxId')] = 'Format XX-XXXXXXX';
  if (!ownershipOk(normalized.ownershipType)) {
    errors[key('ownershipType')] = 'Select Independent, Franchisee, or Corporate';
  }

  const pc = normalized.primaryContact || {};
  if (!pc.name || !pc.name.trim()) errors[key('primaryContact.name')] = 'Required';
  if (!emailOk(pc.email)) errors[key('primaryContact.email')] = 'Invalid email';
  if (!phoneOk(pc.phone)) errors[key('primaryContact.phone')] = 'Invalid phone';

  const brands = Array.isArray(normalized.brands) && normalized.brands.length ? normalized.brands : [emptyBrand()];

  brands.forEach((brand, brandIndex) => {
    const brandKey = (k) => `${idx}.brands.${brandIndex}.${k}`;
    const locationSearch = normalizeLocationSearch(
      brand.locationSearch,
      brand.brandName,
      { commitPending: true, trimSearchName: true }
    );
    brands[brandIndex] = sanitizeBrand({ ...brand, locationSearch });

    if (!brand.brandName || !brand.brandName.trim()) errors[brandKey('brandName')] = 'Required';
    if (!brand.businessCategory) errors[brandKey('businessCategory')] = 'Required';
    if (!pctOk(brand.baselineContribution)) errors[brandKey('baselineContribution')] = '2–10%';
    if (!logoOk(brand)) errors[brandKey('logoFile')] = 'Logo upload is required';
    if (!descOk(brand.description)) {
      errors[brandKey('description')] = 'Required • up to 128 characters';
    }

    if (brand.isOnlineOnly) {
      if (!urlOk(brand.primaryWebsiteUrl)) {
        errors[brandKey('primaryWebsiteUrl')] = 'Must be a valid URL (http/https).';
      }
    } else {
      if (locationSearch.usePublicName === false && !locationSearch.searchName.trim()) {
        errors[brandKey('locationSearch.searchName')] = 'Enter a search name or use the public business name.';
      }
      if (!locationSearch.mode) {
        errors[brandKey('locationSearch.mode')] = 'Choose how broadly to search.';
      }
      if (!locationSearch.values.length) {
        errors[brandKey('locationSearch.values')] = 'Add at least one search area.';
      }
    }
  });

  return { errors, normalized: { ...normalized, brands } };
};

const validateAllForms = (forms) => {
  let errors = {};
  const normalized = [];

  forms.forEach((form, idx) => {
    const { errors: formErrors, normalized: normalizedForm } = validateEinForm(form, idx);
    if (Object.keys(formErrors).length) {
      errors = { ...errors, ...formErrors };
    }
    normalized[idx] = normalizedForm;
  });

  return { ok: Object.keys(errors).length === 0, errors, normalized };
};

const buildBusinessesFromEinForms = (forms = []) => {
  const businesses = [];

  (Array.isArray(forms) ? forms : []).forEach((einForm, einIdx) => {
    (Array.isArray(einForm?.brands) ? einForm.brands : []).forEach((brand, brandIdx) => {
      const locationSearch = normalizeLocationSearch(
        brand?.locationSearch,
        brand?.brandName,
        { commitPending: true, trimSearchName: true }
      );
      const regionalList = locationSearch.values || [];
      const markets = (brand.markets || regionalList.map((m) => ({ marketKey: m, name: m })) || []).map(
        (m) => ({
          marketKey: m.marketKey || m.name || m,
          name: m.name || m.marketKey || m,
          marketType: 'operating_area',
          attested: true,
          coverage: { expectedCount: 0, enrolledCount: 0, coveragePct: 0 },
        })
      );

      const cleanBrandName = (brand.brandName || '').trim();
      const legalEntityName = (einForm.legalBusinessName || '').trim();
      const primaryContact = {
        name: (einForm.primaryContact?.name || '').trim(),
        email: (einForm.primaryContact?.email || '').trim(),
        phone: (einForm.primaryContact?.phone || '').trim(),
      };
      const sanitizedTagline = typeof brand.tagline === 'string' ? brand.tagline.trim() : '';
      const sanitizedDescription = typeof brand.description === 'string' ? brand.description.trim() : '';
      const sanitizedWebsite =
        typeof brand.primaryWebsiteUrl === 'string' ? brand.primaryWebsiteUrl.trim() : '';
      const sanitizedLogoUrl =
        typeof brand.logoUrl === 'string'
          ? brand.logoUrl
          : typeof brand.logo === 'string'
          ? brand.logo
          : '';

      businesses.push({
        legalEntityName,
        ein: einForm.einTaxId,
        ownershipType: einForm.ownershipType,
        primaryContact,
        aliases: einForm.aliases,
        dbaName: cleanBrandName,
        category: brand.businessCategory || 'Other',
        baseContributionPct: Number(brand.baselineContribution || 2),
        tagline: sanitizedTagline,
        description: sanitizedDescription,
        logoUrl: sanitizedLogoUrl,
        isOnlineOnly: !!brand.isOnlineOnly,
        primaryWebsiteUrl: sanitizedWebsite,
        locationSearch: brand.isOnlineOnly
          ? { mode: '', values: [], searchName: '' }
          : {
              mode: locationSearch.mode,
              values: regionalList,
              searchName: locationSearch.searchName || cleanBrandName,
            },
        markets,
        footprint: toFootprint(einForm.ownershipType),
        tempBusinessKey: `${einIdx}-${brandIdx}`,
        ...(typeof brand.tipEligible === 'boolean' ? { tipEligible: brand.tipEligible } : {}),
        ...(brand.restaurantSubtype ? { restaurantSubtype: brand.restaurantSubtype } : {}),
      });
    });
  });

  return businesses;
};

const FrostedInput = ({ style, ...props }) => (
  <input
    className="frosted-input"
    {...props}
    style={{
      width: '100%',
      height: 60,
      borderRadius: 16,
      border: '1px solid rgba(255,255,255,0.10)',
      background: 'rgba(255,255,255,0.12)',
      color: 'white',
      fontSize: 18,
      padding: '0 18px',
      outline: 'none',
      boxSizing: 'border-box',
      ...style,
    }}
  />
);

const FrostedSelect = ({ style, children, ...props }) => (
  <select
    className="frosted-input"
    {...props}
    style={{
      width: '100%',
      height: 60,
      borderRadius: 16,
      border: '1px solid rgba(255,255,255,0.10)',
      background: 'rgba(255,255,255,0.12)',
      color: 'white',
      fontSize: 16,
      padding: '0 16px',
      outline: 'none',
      boxSizing: 'border-box',
      appearance: 'none',
      ...style,
    }}
  >
    {children}
  </select>
);

const FrostedTextArea = ({ rows = 3, style, ...props }) => (
  <textarea
    className="frosted-input"
    rows={rows}
    {...props}
    style={{
      width: '100%',
      borderRadius: 16,
      border: '1px solid rgba(255,255,255,0.10)',
      background: 'rgba(255,255,255,0.12)',
      color: 'white',
      fontSize: 16,
      padding: '14px 18px',
      outline: 'none',
      boxSizing: 'border-box',
      resize: 'vertical',
      ...style,
    }}
  />
);

const TinyBtn = ({ children, style, ...props }) => (
  <button
    {...props}
    style={{
      background: 'rgba(255,255,255,0.12)',
      border: '1px solid rgba(255,255,255,0.18)',
      color: 'white',
      borderRadius: 10,
      padding: '8px 12px',
      cursor: 'pointer',
      fontSize: 14,
      ...style,
    }}
  >
    {children}
  </button>
);

export { emptyEinForm, emptyBrand }; // useful for other steps if needed

export default function Step2Contacts({
  data,
  step1Data = {},
  onNext,
  onPrevious,
  loading,
  isFirstStep,
  onBindNext,
}) {
  const [einForms, setEinForms] = useState([emptyEinForm()]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [errors, setErrors] = useState({});

  // Guards for hydration + binding
  const hydratedKeyRef = useRef('');
  const bindRef = useRef({ onClick: null, disabled: true });

  /* ---------- hydrate when returning (supports both einForms + businesses drafts) ---------- */
  useEffect(() => {
    const hydrated = hydrateEinForms(data);
    if (!hydrated || !hydrated.length) return;

    const key = JSON.stringify(
      hydrated.map((f) => ({
        legalBusinessName: f.legalBusinessName,
        einTaxId: f.einTaxId,
        ownershipType: f.ownershipType,
        aliasCount: (f.aliases || []).length,
        brands: (f.brands || []).map((b) => ({
          brandName: b.brandName,
          businessCategory: b.businessCategory,
          baselineContribution: b.baselineContribution,
          isOnlineOnly: !!b.isOnlineOnly,
          descriptionLength: (b.description || '').length,
          regionalCount: Array.isArray(b?.marketsInput?.regionalList) ? b.marketsInput.regionalList.length : 0,
        })),
      }))
    );

    if (hydratedKeyRef.current === key) return;

    setEinForms(hydrated);
    setActiveIndex(0);
    hydratedKeyRef.current = key;
  }, [data]);

  /* ---------- prefill contact from Step 1 (once) ---------- */
  useEffect(() => {
    if (!step1Data) return;
    setEinForms((prev) =>
      prev.map((f) => {
        const pc = f.primaryContact || {};
        if (pc.name && pc.email && pc.phone) return f;
        return {
          ...f,
          primaryContact: {
            name: pc.name || step1Data.fullName || '',
            email: pc.email || step1Data.email || '',
            phone: pc.phone || step1Data.phone || '',
          },
        };
      })
    );
    // run once on mount for prefill
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = einForms[activeIndex] || emptyEinForm();

  const setActive = (partial) => {
    setEinForms((prev) =>
      prev.map((f, i) => (i === activeIndex ? sanitizeEinForm({ ...f, ...partial }) : f))
    );
  };

  const setActiveBrand = (brandIndex, partial) => {
    setEinForms((prev) =>
      prev.map((form, i) => {
        if (i !== activeIndex) return form;
        const brands = (form.brands || [emptyBrand()]).map((brand, idx) =>
          idx === brandIndex ? sanitizeBrand({ ...brand, ...partial }) : brand
        );
        return sanitizeEinForm({ ...form, brands });
      })
    );
  };

  const addBlank = () => {
    setEinForms((prev) => {
      const next = [...prev, emptyEinForm()];
      setActiveIndex(next.length - 1);
      return next;
    });
  };

  const addClone = () => {
    setEinForms((prev) => {
      const src = prev[activeIndex] || prev[prev.length - 1] || emptyEinForm();
      const clone = sanitizeEinForm({
        ...src,
        einTaxId: '',
        brands: (src.brands || [emptyBrand()]).map((brand) => ({
          ...brand,
          logoFile: null,
          logoPreviewUrl: '',
        })),
      });
      const next = [...prev, clone];
      setActiveIndex(next.length - 1);
      return next;
    });
  };

  const removeEinAt = (idx) => {
    setEinForms((prev) => {
      if (prev.length === 1) return [emptyEinForm()];
      const next = prev.filter((_, i) => i !== idx);
      const newIdx = Math.max(0, Math.min(idx, next.length - 1));
      setActiveIndex(newIdx);
      // reset errors on EIN removal
      setErrors((prevErrors) => {
        const filtered = {};
        Object.entries(prevErrors).forEach(([key, value]) => {
          const [formIdx] = key.split('.');
          if (Number(formIdx) === idx) return;
          const updatedKey =
            Number(formIdx) > idx
              ? `${Number(formIdx) - 1}.${key.split('.').slice(1).join('.')}`
              : key;
          filtered[updatedKey] = value;
        });
        return filtered;
      });
      return next.length ? next : [emptyEinForm()];
    });
  };

  const addBrandUnderCurrentEIN = () => {
    setEinForms((prev) =>
      prev.map((form, idx) => {
        if (idx !== activeIndex) return form;
        const brands = [...(form.brands || []), emptyBrand()];
        return sanitizeEinForm({ ...form, brands });
      })
    );
  };

  const removeBrandAt = (brandIndex) => {
    setEinForms((prev) =>
      prev.map((form, idx) => {
        if (idx !== activeIndex) return form;
        const brands = (form.brands || [emptyBrand()]).filter((_, i) => i !== brandIndex);
        const safeBrands = brands.length ? brands : [emptyBrand()];
        return sanitizeEinForm({ ...form, brands: safeBrands });
      })
    );

    setErrors((prevErrors) => {
      const filtered = {};
      Object.entries(prevErrors).forEach(([key, value]) => {
        const parts = key.split('.');
        const [formIdx, scope, brandIdx, ...rest] = parts;
        if (Number(formIdx) !== activeIndex || scope !== 'brands') {
          filtered[key] = value;
          return;
        }
        if (Number(brandIdx) === brandIndex) return;
        if (Number(brandIdx) > brandIndex) {
          filtered[`${formIdx}.brands.${Number(brandIdx) - 1}.${rest.join('.')}`] = value;
        } else {
          filtered[key] = value;
        }
      });
      return filtered;
    });
  };

  const handleBrandLogoChange = async (brandIndex, e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      setErrors((prev) => ({
        ...prev,
        [`${activeIndex}.brands.${brandIndex}.logoFile`]: 'Please upload a logo image file, such as a JPG, PNG, or WebP.',
      }));
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setErrors((prev) => ({
        ...prev,
        [`${activeIndex}.brands.${brandIndex}.logoFile`]: 'Image too large. Please choose a file that is 2 MB or smaller.',
      }));
      if (e?.target) e.target.value = '';
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setActiveBrand(brandIndex, { logoFile: file, logoPreviewUrl: previewUrl, logoUrl: '' });
    setErrors((prev) => {
      const { [`${activeIndex}.brands.${brandIndex}.logoFile`]: _drop, ...rest } = prev;
      return rest;
    });

    try {
      const uploaded = await nfpApi.uploadAsset(file, { kind: 'brand-logo', folder: 'brand-logos' });
      const uploadedUrl = uploaded?.url || uploaded?.location || uploaded?.fileUrl || '';
      if (!uploadedUrl) {
        throw new Error('Logo upload did not return a URL');
      }
      setActiveBrand(brandIndex, { logoFile: null, logoPreviewUrl: previewUrl, logoUrl: uploadedUrl });
      setErrors((prev) => {
        const { [`${activeIndex}.brands.${brandIndex}.logoFile`]: _drop, ...rest } = prev;
        return rest;
      });
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        [`${activeIndex}.brands.${brandIndex}.logoFile`]: err?.message || "We couldn't upload the selected logo. Please try again or choose a different image.",
      }));
      setActiveBrand(brandIndex, { logoFile: null, logoPreviewUrl: '', logoUrl: '' });
    }
  };

  const handleClick = useCallback(() => {
    const { ok, errors: allErr, normalized } = validateAllForms(einForms);
    setErrors(allErr);
    if (!ok || loading) return;

    const businesses = buildBusinessesFromEinForms(normalized);

    const allBrandsOnlineOnly = normalized.every((einForm) =>
      einForm.brands.every((brand) => !!brand.isOnlineOnly)
    );
    const nextStepOverride = allBrandsOnlineOnly ? 4 : undefined;

    setEinForms(normalized);
    onNext({ einForms: normalized, businesses }, nextStepOverride);
  }, [einForms, loading, onNext]);

  /* ---------- STABLE onBindNext to prevent update loop ---------- */
  const clickRef = useRef(() => {});
  useEffect(() => {
    clickRef.current = handleClick;
  }, [handleClick]);

  const stableClick = useCallback((...args) => {
    return clickRef.current?.(...args);
  }, []);

  const activeValidationErrors = useMemo(
    () => validateEinForm(active, activeIndex).errors,
    [active, activeIndex]
  );

  const canProceed = useMemo(() => {
    return Object.keys(activeValidationErrors).length === 0 && !loading;
  }, [activeValidationErrors, loading]);

  useEffect(() => {
    if (!onBindNext) return;

    const payload = { onClick: stableClick, disabled: !canProceed };
    const prev = bindRef.current;

    if (prev.onClick !== payload.onClick || prev.disabled !== payload.disabled) {
      onBindNext(payload);
      bindRef.current = payload;
    }
  }, [onBindNext, stableClick, canProceed]);

  useEffect(() => {
    return () => {
      onBindNext?.({ onClick: null, disabled: true });
      bindRef.current = { onClick: null, disabled: true };
    };
  }, [onBindNext]);

  const err = (k) => errors[`${activeIndex}.${k}`];
  const brandErr = (brandIndex, k) => errors[`${activeIndex}.brands.${brandIndex}.${k}`];

  /* ---------- UI ---------- */
  return (
    <div style={{ color: 'white' }}>
      <h2 style={{ marginTop: 0, marginBottom: 8 }}>Add a Business</h2>
      <p style={{ marginTop: 0, opacity: 0.9 }}>
        Capture each legal entity (EIN). Within each EIN, add every public-facing business that should appear to
        consumers.
      </p>

      {/* EIN tabs */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        {einForms.map((f, idx) => {
          const primaryBrandName = f?.brands?.[0]?.brandName;
          return (
            <button
              key={idx}
              onClick={() => { setActiveIndex(idx); }}
              style={{
                background: idx === activeIndex ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.10)',
                border: idx === activeIndex ? `1px solid ${PEACH}` : '1px solid rgba(255,255,255,0.18)',
                color: 'white',
                borderRadius: 12,
                padding: '8px 12px',
                cursor: 'pointer',
              }}
            >
              EIN {idx + 1}{primaryBrandName ? ` — ${primaryBrandName}` : ''}
            </button>
          );
        })}
        {einForms.length > 1 && (
          <TinyBtn onClick={() => removeEinAt(activeIndex)}>Remove Current EIN</TinyBtn>
        )}
      </div>

      {/* EIN-level identity */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>Legal Business Name *</label>
          <FrostedInput
            value={active.legalBusinessName}
            onChange={(e) => setActive({ legalBusinessName: e.target.value })}
            placeholder="Legal entity name"
            aria-required="true"
          />
          {err('legalBusinessName') && <div style={{ color: '#ffdada', marginTop: 6 }}>{err('legalBusinessName')}</div>}
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>EIN / Tax ID *</label>
          <FrostedInput
            value={active.einTaxId}
            onChange={(e) => setActive({ einTaxId: e.target.value })}
            onBlur={(e) =>
              setActive({
                einTaxId: formatEin(e.target.value || ''),
              })
            }
            placeholder="XX-XXXXXXX"
            aria-required="true"
          />
          {err('einTaxId') && <div style={{ color: '#ffdada', marginTop: 6 }}>{err('einTaxId')}</div>}
        </div>
      </div>

      {/* Ownership */}
      <div style={{ marginTop: 16 }}>
        <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>Ownership Type *</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          {OWNERSHIP.map((o) => (
            <button
              key={o}
              onClick={() => setActive({ ownershipType: o })}
              style={{
                background: active.ownershipType === o ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.10)',
                border: active.ownershipType === o ? `1px solid ${PEACH}` : '1px solid rgba(255,255,255,0.18)',
                color: 'white',
                borderRadius: 12,
                padding: '8px 12px',
                cursor: 'pointer',
              }}
              aria-pressed={active.ownershipType === o}
            >
              {o}
            </button>
          ))}
        </div>
        <div style={{ marginTop: -4, marginBottom: 10, opacity: 0.9 }}>
          {OWNERSHIP_HELP[active.ownershipType] || OWNERSHIP_HELP.Independent}
        </div>
        {err('ownershipType') && <div style={{ color: '#ffdada', marginTop: 6 }}>{err('ownershipType')}</div>}
      </div>

      {/* Primary contact */}
      <h3 style={{ marginTop: 16, marginBottom: 6 }}>Primary Contact</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <FrostedInput
          placeholder="Name *"
          value={active.primaryContact?.name || ''}
          onChange={(e) => setActive({ primaryContact: { ...active.primaryContact, name: e.target.value } })}
          aria-required="true"
        />
        <FrostedInput
          placeholder="Email *"
          value={active.primaryContact?.email || ''}
          onChange={(e) => setActive({ primaryContact: { ...active.primaryContact, email: e.target.value } })}
          aria-required="true"
        />
        <FrostedInput
          placeholder="Phone *"
          value={active.primaryContact?.phone || ''}
          onChange={(e) => setActive({ primaryContact: { ...active.primaryContact, phone: e.target.value } })}
          aria-required="true"
        />
      </div>
      {(err('primaryContact.name') || err('primaryContact.email') || err('primaryContact.phone')) && (
        <div style={{ color: '#ffdada', marginTop: 6 }}>
          {err('primaryContact.name') || err('primaryContact.email') || err('primaryContact.phone')}
        </div>
      )}

      {/* Brand blocks */}
      <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
        {(active.brands || []).map((brand, bIndex) => (
          <BrandBlock
            key={bIndex}
            brand={brand}
            index={bIndex}
            onUpdate={(partial) => setActiveBrand(bIndex, partial)}
            onRemove={() => removeBrandAt(bIndex)}
            showRemove={bIndex > 0}
            errorFor={(field) => brandErr(bIndex, field)}
            onLogoChange={(event) => handleBrandLogoChange(bIndex, event)}
          />
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        <button
          type="button"
          onClick={addBrandUnderCurrentEIN}
          style={{
            background: PEACH,
            border: `1px solid ${PEACH}`,
            color: '#1B1B1B',
            padding: '12px 18px',
            borderRadius: 14,
            cursor: 'pointer',
            fontSize: 16,
          }}
        >
          ➕ Add another Business Name under this EIN
        </button>
      </div>

      {/* bottom controls */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 32,
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {onPrevious && !isFirstStep && (
            <TinyBtn
              onClick={() => {
                const normalizedDraft = einForms.map(sanitizeEinForm);
                onPrevious?.({
                  einForms: normalizedDraft,
                  businesses: buildBusinessesFromEinForms(normalizedDraft),
                });
              }}
            >
              Back
            </TinyBtn>
          )}
          <TinyBtn onClick={addBlank} style={{ background: 'rgba(255,255,255,0.12)' }}>
            ➕ Add another Legal Entity / EIN
          </TinyBtn>
          <TinyBtn onClick={addClone} style={{ background: 'rgba(255,255,255,0.12)' }}>
            ⧉ Copy this EIN to new EIN
          </TinyBtn>
        </div>

      </div>
    </div>
  );
}

function BrandBlock({ brand, index, onUpdate, onRemove, showRemove, errorFor, onLogoChange }) {
  const descriptionLength = (brand.description || '').length;
  const locationSearch = normalizeLocationSearch(brand.locationSearch, brand.brandName, { commitPending: false });
  const hasPhysicalLocations = !brand.isOnlineOnly;
  const valueMode = locationSearch.mode || '';
  const values = locationSearch.values || [];
  const pendingInput = locationSearch.pendingInput || '';
  const usesPublicSearchName = locationSearch.usePublicName !== false;

  const updateLocationSearch = (patch) => {
    const next = normalizeLocationSearch(
      { ...locationSearch, ...patch },
      brand.brandName,
      { commitPending: false }
    );
    onUpdate({ locationSearch: next });
  };

  const commitPendingValue = () => {
    const next = normalizeLocationSearch(locationSearch, brand.brandName, { commitPending: true });
    if (
      next.pendingInput === locationSearch.pendingInput &&
      JSON.stringify(next.values) === JSON.stringify(locationSearch.values)
    ) {
      return;
    }
    onUpdate({ locationSearch: next });
  };

  return (
    <div
      style={{
        border: '1px solid rgba(255,255,255,0.18)',
        borderRadius: 18,
        padding: 18,
        background: 'rgba(255,255,255,0.08)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Business {index + 1}</h3>
        {showRemove && (
          <TinyBtn onClick={onRemove} style={{ fontSize: 13, padding: '6px 10px' }}>
            Remove this business
          </TinyBtn>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>Public / Business Name *</label>
          <FrostedInput
            value={brand.brandName}
            onChange={(e) =>
              onUpdate({
                brandName: e.target.value,
                locationSearch: {
                  ...locationSearch,
                  searchName: usesPublicSearchName
                    ? e.target.value
                    : locationSearch.searchName,
                  usePublicName: usesPublicSearchName,
                },
              })
            }
            placeholder="What customers know you by"
            aria-required="true"
          />
          {errorFor('brandName') && <div style={{ color: '#ffdada', marginTop: 6 }}>{errorFor('brandName')}</div>}
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>Category *</label>
          <FrostedSelect
            value={brand.businessCategory}
            onChange={(e) => onUpdate({ businessCategory: e.target.value })}
            aria-required="true"
          >
            <option value="">Select a category</option>
            {BRAND_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </FrostedSelect>
          {errorFor('businessCategory') && (
            <div style={{ color: '#ffdada', marginTop: 6 }}>{errorFor('businessCategory')}</div>
          )}
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>
            Contribution % (excl Fees) *
          </label>
          <FrostedSelect
            value={brand.baselineContribution}
            onChange={(e) => onUpdate({ baselineContribution: e.target.value })}
            aria-required="true"
          >
            {baselineOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </FrostedSelect>
          {errorFor('baselineContribution') && (
            <div style={{ color: '#ffdada', marginTop: 6 }}>{errorFor('baselineContribution')}</div>
          )}
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>Business Logo (image upload) *</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 10, alignItems: 'center' }}>
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: 12,
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.18)',
                background: 'rgba(255,255,255,0.10)',
              }}
            >
              {brand.logoPreviewUrl ? (
                <img src={brand.logoPreviewUrl} alt="Logo preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : brand.logoUrl ? (
                <img src={brand.logoUrl} alt="Logo url preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : null}
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={onLogoChange}
              aria-required="true"
              style={{
                width: '100%',
                height: 60,
                borderRadius: 16,
                border: '1px solid rgba(255,255,255,0.10)',
                background: 'rgba(255,255,255,0.12)',
                color: 'white',
                fontSize: 16,
                padding: '14px 18px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          {errorFor('logoFile') && <div style={{ color: '#ffdada', marginTop: 6 }}>{errorFor('logoFile')}</div>}
          {!errorFor('logoFile') && (
            <div style={{ color: 'rgba(255,255,255,0.72)', marginTop: 6, fontSize: 14 }}>
              Max file size: 2 MB.
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 }}>
        <div>
          <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>Tagline (optional)</label>
          <FrostedInput
            placeholder="Short tagline"
            value={brand.tagline}
            onChange={(e) => onUpdate({ tagline: e.target.value })}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>
            Description (max 128 chars) *
          </label>
          <FrostedTextArea
            rows={3}
            placeholder="Describe your business in a sentence or two"
            value={brand.description}
            onChange={(e) => onUpdate({ description: (e.target.value || '').slice(0, 128) })}
            aria-required="true"
          />
          <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
            {descriptionLength}/128
          </div>
          {errorFor('description') && <div style={{ color: '#ffdada', marginTop: 6 }}>{errorFor('description')}</div>}
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <div style={{ display: 'block', marginBottom: 8, opacity: 0.95, fontWeight: 600 }}>
          Physical Presence
        </div>
        <div style={{ marginBottom: 10, opacity: 0.85, fontSize: 14 }}>
          Does this business have physical locations?
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() =>
              onUpdate({
                isOnlineOnly: false,
                locationSearch: {
                  ...locationSearch,
                  searchName: locationSearch.searchName || brand.brandName || '',
                  usePublicName: locationSearch.usePublicName !== false,
                },
              })
            }
            style={toggleButtonStyle(hasPhysicalLocations)}
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() =>
              onUpdate({
                isOnlineOnly: true,
                primaryWebsiteUrl: brand.primaryWebsiteUrl || '',
                locationSearch: { mode: '', values: [], searchName: '', pendingInput: '' },
                marketsInput: { regionalList: [] },
                markets: [],
              })
            }
            style={toggleButtonStyle(!hasPhysicalLocations)}
          >
            No, this business is online-only
          </button>
        </div>
      </div>

      {brand.isOnlineOnly ? (
        <div style={{ marginTop: 16 }}>
          <h4 style={{ margin: '0 0 6px 0' }}>Online Business Verification</h4>
          <p style={{ marginTop: 0, marginBottom: 10, opacity: 0.85, fontSize: 14 }}>
            Online-only businesses do not enroll physical locations and will skip location review.
          </p>
          <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>Primary Website URL *</label>
          <FrostedInput
            value={brand.primaryWebsiteUrl}
            onChange={(e) => onUpdate({ primaryWebsiteUrl: e.target.value })}
            placeholder="https://yourbusiness.com"
            aria-required="true"
          />
          <div style={{ marginTop: 6, opacity: 0.75, fontSize: 13 }}>
            Enter the main website customers use to access or verify this business.
          </div>
          {errorFor('primaryWebsiteUrl') && (
            <div style={{ color: '#ffdada', marginTop: 6 }}>{errorFor('primaryWebsiteUrl')}</div>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 16 }}>
          <h4 style={{ margin: '0 0 6px 0' }}>Location Search Setup</h4>
          <p style={{ marginTop: 0, marginBottom: 12, opacity: 0.85, fontSize: 14 }}>
            Tell us how broadly to search for this business’s locations. We’ll use the public-facing business name and your selected search area to find candidate locations for enrollment, so you don’t have to enter each one manually.
          </p>

          <div>
            <label style={{ display: 'block', marginBottom: 8, opacity: 0.95, fontWeight: 600 }}>
              Search Granularity
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {SEARCH_GRANULARITY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() =>
                    updateLocationSearch({
                      mode: option.value,
                      values: option.value === valueMode ? values : [],
                      pendingInput: '',
                    })
                  }
                  style={toggleButtonStyle(valueMode === option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {errorFor('locationSearch.mode') && (
              <div style={{ color: '#ffdada', marginTop: 6 }}>{errorFor('locationSearch.mode')}</div>
            )}
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>Search Name</label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 10,
                color: 'rgba(255,255,255,0.86)',
                fontSize: 14,
              }}
            >
              <input
                type="checkbox"
                checked={usesPublicSearchName}
                onChange={(e) => {
                  const checked = e.target.checked;
                  updateLocationSearch({
                    usePublicName: checked,
                    searchName: checked ? (brand.brandName || '') : (locationSearch.searchName || brand.brandName || ''),
                  });
                }}
              />
              Use public business name
            </label>
            <FrostedInput
              value={usesPublicSearchName ? (brand.brandName || '') : locationSearch.searchName}
              onChange={(e) => updateLocationSearch({ searchName: e.target.value, usePublicName: false })}
              placeholder="Enter a different search name"
              disabled={usesPublicSearchName}
              style={usesPublicSearchName ? { opacity: 0.72, cursor: 'not-allowed' } : undefined}
            />
            <div style={{ marginTop: 6, opacity: 0.75, fontSize: 13 }}>
              Defaults to the public business name above. Turn this off only if customers know this business by a different name when searching for locations.
            </div>
            {errorFor('locationSearch.searchName') && (
              <div style={{ color: '#ffdada', marginTop: 6 }}>{errorFor('locationSearch.searchName')}</div>
            )}
          </div>

          {valueMode === 'state' && (
            <StateValuesInput
              inputId={`brand-${index}-states`}
              values={values}
              onCommit={(nextValues) => updateLocationSearch({ values: nextValues, pendingInput: '' })}
              onRemove={(nextValues) => updateLocationSearch({ values: nextValues })}
            />
          )}

          {valueMode === 'area' && (
            <SearchValuesInput
              inputId={`brand-${index}-areas`}
              label="City"
              helperText="Add cities in City, ST format so we can match the right location."
              microcopy="Add one city at a time, then click Add. Examples: Lake Forest, IL; Lake Forest, CA."
              placeholder="Enter a city and state, like Lake Forest, IL"
              values={values}
              pendingInput={pendingInput}
              onPendingChange={(next) => updateLocationSearch({ pendingInput: next })}
              onAdd={commitPendingValue}
              onRemove={(nextValues) => updateLocationSearch({ values: nextValues })}
              isAddDisabled={!String(pendingInput || '').trim()}
            />
          )}

          {valueMode === 'zip' && (
            <SearchValuesInput
              inputId={`brand-${index}-zips`}
              label="ZIP Code"
              helperText="Add the ZIP codes where this business operates physical locations."
              microcopy="Add one ZIP code at a time, then click Add. At least one ZIP code is required."
              placeholder="Enter a ZIP code"
              values={values}
              pendingInput={pendingInput}
              onPendingChange={(next) => updateLocationSearch({ pendingInput: next.replace(/\D+/g, '').slice(0, 5) })}
              onAdd={commitPendingValue}
              onRemove={(nextValues) => updateLocationSearch({ values: nextValues })}
              isAddDisabled={!/^\d{5}$/.test(String(pendingInput || '').trim())}
            />
          )}

          {errorFor('locationSearch.values') && (
            <div style={{ color: '#ffdada', marginTop: 6 }}>{errorFor('locationSearch.values')}</div>
          )}
        </div>
      )}
    </div>
  );
}

function SearchValuesInput({
  inputId,
  label,
  helperText,
  microcopy,
  placeholder,
  values = [],
  pendingInput = '',
  onPendingChange,
  onAdd,
  onRemove,
  isAddDisabled = true,
}) {
  return (
    <div style={{ marginTop: 14 }}>
      <label style={{ display: 'block', marginBottom: 6, opacity: 0.95, fontWeight: 600 }}>{label}</label>
      <div style={{ marginBottom: 6, opacity: 0.85, fontSize: 14 }}>{helperText}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'start' }}>
        <FrostedInput
          id={inputId}
          value={pendingInput}
          onChange={(e) => onPendingChange?.(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onAdd?.();
            }
          }}
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={() => onAdd?.()}
          disabled={isAddDisabled}
          style={{
            height: 60,
            borderRadius: 16,
            border: `1px solid ${isAddDisabled ? 'rgba(255,255,255,0.16)' : PEACH}`,
            background: isAddDisabled ? 'rgba(255,255,255,0.10)' : PEACH,
            color: isAddDisabled ? 'rgba(255,255,255,0.65)' : '#1B1B1B',
            fontWeight: 700,
            padding: '0 18px',
            cursor: isAddDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          Add
        </button>
      </div>
      <div style={{ marginTop: 6, opacity: 0.75, fontSize: 13 }}>{microcopy}</div>

      {!!values.length && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          {values.map((value, idx) => (
            <span
              key={`${value}-${idx}`}
              style={{
                background: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.26)',
                borderRadius: 12,
                padding: '6px 10px',
              }}
            >
              {value}
              <button
                type="button"
                onClick={() => onRemove?.(values.filter((_, valueIdx) => valueIdx !== idx))}
                style={{
                  marginLeft: 6,
                  background: 'transparent',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                }}
                aria-label={`Remove ${value}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function StateValuesInput({
  inputId,
  values = [],
  onCommit,
  onRemove,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [stagedValues, setStagedValues] = useState([]);
  const filteredStates = STATE_OPTIONS.filter(([code, label]) => {
    const needle = String(query || '').trim().toLowerCase();
    if (!needle) return true;
    return code.toLowerCase().includes(needle) || label.toLowerCase().includes(needle);
  });
  const canAdd = stagedValues.length > 0;
  const triggerLabel = stagedValues.length
    ? `${stagedValues.length} state${stagedValues.length === 1 ? '' : 's'} selected`
    : 'Choose state(s)';

  useEffect(() => {
    setStagedValues((prev) => prev.filter((code) => !values.includes(code)));
  }, [values]);

  const toggleStagedValue = (code) => {
    setStagedValues((prev) =>
      prev.includes(code) ? prev.filter((value) => value !== code) : [...prev, code]
    );
  };

  return (
    <div style={{ marginTop: 14 }}>
      <label style={{ display: 'block', marginBottom: 6, opacity: 0.95, fontWeight: 600 }}>State</label>
      <div style={{ marginBottom: 6, opacity: 0.85, fontSize: 14 }}>
        Add one or more states where this business operates physical locations.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'start' }}>
        <div>
          <button
            id={inputId}
            type="button"
            onClick={() => setIsOpen((open) => !open)}
            style={{
              width: '100%',
              height: 60,
              borderRadius: 16,
              border: `1px solid ${isOpen ? PEACH : 'rgba(255,255,255,0.10)'}`,
              background: 'rgba(255,255,255,0.12)',
              color: stagedValues.length ? 'white' : 'rgba(255,255,255,0.75)',
              fontSize: 18,
              padding: '0 18px',
              outline: 'none',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              textAlign: 'left',
              cursor: 'pointer',
            }}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            <span>{triggerLabel}</span>
            <span style={{ opacity: 0.75, fontSize: 14 }}>{isOpen ? '▲' : '▼'}</span>
          </button>

          {isOpen && (
            <div
              style={{
                marginTop: 8,
                borderRadius: 18,
                border: '1px solid rgba(255,255,255,0.18)',
                background: 'rgba(22, 54, 61, 0.98)',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 10px 24px rgba(0,0,0,0.18)',
                padding: 10,
              }}
            >
              <FrostedInput
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search states"
                style={{ height: 48, fontSize: 16, marginBottom: 8 }}
              />
              <div
                role="listbox"
                style={{
                  maxHeight: 160,
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  paddingRight: 4,
                }}
              >
                {filteredStates.length ? (
                  filteredStates.map(([code, label]) => {
                    const isCommitted = values.includes(code);
                    const isStaged = stagedValues.includes(code);
                    const selected = isCommitted || isStaged;
                    return (
                      <button
                        key={code}
                        type="button"
                        onClick={() => {
                          if (isCommitted) {
                            onRemove?.(values.filter((value) => value !== code));
                            return;
                          }
                          toggleStagedValue(code);
                        }}
                        style={{
                          width: '100%',
                          borderRadius: 12,
                          border: `1px solid ${selected ? PEACH : 'rgba(255,255,255,0.12)'}`,
                          background: selected ? 'rgba(235,164,119,0.18)' : 'rgba(255,255,255,0.08)',
                          color: 'white',
                          padding: '10px 12px',
                          textAlign: 'left',
                          cursor: 'pointer',
                          fontSize: 15,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span>{label} ({code})</span>
                        {selected ? (
                          <span style={{ fontSize: 13, opacity: 0.9 }}>
                            {isCommitted ? 'Added' : 'Selected'}
                          </span>
                        ) : null}
                      </button>
                    );
                  })
                ) : (
                  <div
                    style={{
                      borderRadius: 12,
                      border: '1px solid rgba(255,255,255,0.10)',
                      background: 'rgba(255,255,255,0.06)',
                      color: 'rgba(255,255,255,0.78)',
                      padding: '12px 14px',
                      fontSize: 14,
                    }}
                  >
                    No matching states.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            onCommit?.([...values, ...stagedValues]);
            setStagedValues([]);
            setIsOpen(false);
            setQuery('');
          }}
          disabled={!canAdd}
          style={{
            height: 60,
            borderRadius: 16,
            border: `1px solid ${canAdd ? PEACH : 'rgba(255,255,255,0.16)'}`,
            background: canAdd ? PEACH : 'rgba(255,255,255,0.10)',
            color: canAdd ? '#1B1B1B' : 'rgba(255,255,255,0.65)',
            fontWeight: 700,
            padding: '0 18px',
            cursor: canAdd ? 'pointer' : 'not-allowed',
          }}
        >
          Add
        </button>
      </div>
      <div style={{ marginTop: 6, opacity: 0.75, fontSize: 13 }}>
        Open the list, click states to select or deselect them, then click Add to add them all at once.
      </div>

      {!!values.length && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          {values.map((value, idx) => {
            const label = STATE_LABEL_BY_CODE[value] || value;
            return (
              <span
                key={`${value}-${idx}`}
                style={{
                  background: 'rgba(255,255,255,0.18)',
                  border: '1px solid rgba(255,255,255,0.26)',
                  borderRadius: 12,
                  padding: '6px 10px',
                }}
              >
                {label}
                <button
                  type="button"
                  onClick={() => onRemove?.(values.filter((_, valueIdx) => valueIdx !== idx))}
                  style={{
                    marginLeft: 6,
                    background: 'transparent',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  aria-label={`Remove ${label}`}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

function toggleButtonStyle(isActive) {
  return {
    background: isActive ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.10)',
    border: isActive ? `1px solid ${PEACH}` : '1px solid rgba(255,255,255,0.18)',
    color: 'white',
    borderRadius: 12,
    padding: '10px 14px',
    cursor: 'pointer',
    fontWeight: 600,
  };
}
